/**
 * Last-digit helpers. Deriv's tick `pip_size` is the number of decimals the
 * quote must be displayed with, so the last digit is taken from the quote
 * formatted with exactly that many decimals (625.10 -> "0", not "1").
 */

export function formatQuote(quote: number, pipSize: number): string {
  return quote.toFixed(Math.max(0, Math.min(10, Math.round(pipSize))));
}

export function lastDigit(quote: number, pipSize: number): number {
  const s = formatQuote(quote, pipSize);
  return Number(s.charAt(s.length - 1));
}

/** Convert active_symbols pip_size (e.g. 0.001) into decimals count (3). */
export function pipSizeToDecimals(pip: number): number {
  if (pip >= 1) return 0;
  return Math.max(0, Math.round(-Math.log10(pip)));
}

export interface DigitStats {
  total: number;
  even: number;
  odd: number;
  evenPct: number;
  oddPct: number;
  /** Count per digit 0..9 */
  distribution: number[];
  /** Current streak of same parity at the most recent end. */
  parityStreak: { parity: 'EVEN' | 'ODD' | null; length: number };
}

/** `digits` ordered newest first. Purely descriptive — no predictive value. */
export function digitStats(digits: readonly number[]): DigitStats {
  const distribution = Array.from({ length: 10 }, () => 0);
  let even = 0;
  for (const d of digits) {
    distribution[d] = (distribution[d] ?? 0) + 1;
    if (d % 2 === 0) even++;
  }
  const total = digits.length;
  const odd = total - even;
  let streakParity: 'EVEN' | 'ODD' | null = null;
  let streak = 0;
  for (const d of digits) {
    const p = d % 2 === 0 ? 'EVEN' : 'ODD';
    if (streakParity === null) {
      streakParity = p;
      streak = 1;
    } else if (p === streakParity) streak++;
    else break;
  }
  return {
    total,
    even,
    odd,
    evenPct: total ? Math.round((even / total) * 1000) / 10 : 0,
    oddPct: total ? Math.round((odd / total) * 1000) / 10 : 0,
    distribution,
    parityStreak: { parity: streakParity, length: streak },
  };
}

export type TickMove = 'UP' | 'DOWN' | 'FLAT';

/** `quotes` ordered newest first. */
export function tickMoves(quotes: readonly number[]): TickMove[] {
  const out: TickMove[] = [];
  for (let i = 0; i < quotes.length - 1; i++) {
    const a = quotes[i]!;
    const b = quotes[i + 1]!;
    out.push(a > b ? 'UP' : a < b ? 'DOWN' : 'FLAT');
  }
  return out;
}
